import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import './style.css'
import { System } from '../../context/SystemContext';
import { getBackendUrl } from '../../env'
import { useAuthContext } from '../../hooks/useAuthContext';
import { useDevicesContext } from '../../hooks/useDevicesContext';
import { useSystemsContext } from '../../hooks/useSystemsContext';
import { fetchDevices, getDevicesInSystem, getDevicesWithoutSystem, syncDevicesWithSystem } from '../../helper/deviceHelper';
import { checkForCompleteSystems } from '../../helper/programHelper';
import SensorGroup from './SensorGroup';
import { Program } from '../../types';
import DoserSettings from './DoserSettings';
import AutomationSafetySettings from './AutomationSafetySettings';
import {
    DEFAULT_AUTOMATION_SAFETY,
    mergeAutomationSafetyIntoAdvancedOptions,
} from '@/lib/automationSafetyDefaults';
import { isOutletDeviceType } from '@/lib/outletDeviceTypes';
import ManualProgram from '../ManualProgram'
import Select, { type MultiValue, type SingleValue, type StylesConfig } from 'react-select';
import Snackbar from '../Snackbar/Snackbar';
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { hydroReactSelectStyles } from '@/lib/hydroReactSelectStyles'

type SelectOption = { value: string; label: string }

const settingsSelectMultiStyles = hydroReactSelectStyles as StylesConfig<SelectOption, true>
const settingsSelectSingleStyles = hydroReactSelectStyles as StylesConfig<SelectOption, false>

const SYSTEM_SETTINGS_PORTAL_ID = 'hydro-system-settings-portal'

function getOrCreateSystemSettingsPortalRoot(): HTMLElement | null {
    if (typeof document === 'undefined') return null
    let el = document.getElementById(SYSTEM_SETTINGS_PORTAL_ID)
    if (!el) {
        el = document.createElement('div')
        el.id = SYSTEM_SETTINGS_PORTAL_ID
        document.documentElement.appendChild(el)
    }
    return el
}

interface SystemFormProps {
    system: System;
    setSystem: React.Dispatch<React.SetStateAction<System | null>>;
    systemPopup: boolean;
    setSystemPopup: React.Dispatch<React.SetStateAction<boolean>>;
    newSystem: boolean;
    setNewSystem: React.Dispatch<React.SetStateAction<boolean>>;
}
let options = {
        lights:{
          light_start_time: '06:00',
          light_end_time: '18:00'
        },
        dose_amount: 10,
        time_between_doses: {
            hour: 0,
            minute: 0,
            seconds: 30
        },
        time_before_readjustment: {
            hour: 0,
            minute: 10,
            seconds: 0
        },
        tolerances: {
            ph: [.25,.25],
            ec: [.01,.01],
            temperature: [2,2],
            humidity:[5,5]
        },
        mixing_time:{
            minute: 0,
            seconds: 30
        },
        pump_all_day: true,
        pump_start_time: '06:00',
        pump_end_time: '18:00',
        pump_duration: {
            minute: 0,
            seconds: 30
        },
        pump_interval: {
            hour: 0,
            minute: 10
        },
        automation_safety: {
            toggle: { ...DEFAULT_AUTOMATION_SAFETY.toggle },
            dose: { ...DEFAULT_AUTOMATION_SAFETY.dose },
            hysteresis: { ...DEFAULT_AUTOMATION_SAFETY.hysteresis },
        },
    } 
const SystemSettings = ({system, setSystem, systemPopup, setSystemPopup, newSystem, setNewSystem}: SystemFormProps) => {
    const { user }  = useAuthContext()
    const { dispatch: systemsDispatch } = useSystemsContext()
    const { dispatch: deviceDispatch } = useDevicesContext()
    //States
    const [selectedDevices, setSelectedDevices] = useState<SelectOption[]>([])  
    const [programType, setProgramType] = useState<string>('Automatic')
    const [program, setProgram] = useState<Program | any>({
        _id:'',
        name:'',
        temperature: 0,
        humidity: 0,
        ec: 0,
        ph: 0,
        lights_on: 0
    })
    const [devicesInSystem, setDevicesInSystem] = useState<any[]>([])
    const [devicesNotInASystem, setDevicesNotInASystem] = useState<any[]>([])
    const [completedSystems, setCompletedSystems] = useState<any[]>([])
    const [programList, setProgramList] = useState<Program[]>([])
    const [advancedOptionsState, setAdvancedOptionsState] = useState<any>(options)
    const [systemName, setSystemName] = useState<string>('')
    const [reservoirSize, setReservoirSize] = useState<number>()
    const [description, setDescription] = useState<string>('')
    const [sensorGroups, setSensorGroups] = useState<any[]>([])
    const [manualControls, setManualControls] = useState<any[]>([])
    const [snackbarProperties, setSnackbarProperties] = useState({ open: false, success: false, message: '' });

    const [settingsPortalEl] = useState<HTMLElement | null>(() =>
        typeof document !== 'undefined' ? getOrCreateSystemSettingsPortalRoot() : null
    )

    /** Same as device settings modal: scrolled page + fixed layer needs body freeze and portal off <body>. */
    useLayoutEffect(() => {
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0
        const body = document.body
        const html = document.documentElement
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
        }
        body.style.position = 'fixed'
        body.style.top = `-${scrollY}px`
        body.style.left = '0'
        body.style.right = '0'
        body.style.width = '100%'
        body.style.overflow = 'hidden'
        html.style.overflow = 'hidden'

        return () => {
            body.style.position = prev.position
            body.style.top = prev.top
            body.style.left = prev.left
            body.style.right = prev.right
            body.style.width = prev.width
            body.style.overflow = prev.overflow
            html.style.overflow = prev.htmlOverflow
            window.scrollTo(0, scrollY)
        }
    }, [])

    const getData = async () => {
        if(user){
            //Grab devices in system
            const devices = await getDevicesInSystem(user, system._id)
            setDevicesInSystem(devices ?? [])

            const devicesNotInSystem = await getDevicesWithoutSystem(user)
            setDevicesNotInASystem(devicesNotInSystem ?? [])
        }
    }

    const systemId = system && typeof system === 'object' && '_id' in system ? (system as { _id: string })._id : null

    useEffect(() => {
        if (!newSystem && systemId) {
            void getData()
            setSensorGroups(system.sensor_groups ?? [])
            setProgramType(system.programType ?? 'Manual')
            if (system.program !== undefined) {
                setProgram(system.program)
            }
            if (Array.isArray(system.manualControls)) {
                setManualControls(system.manualControls)
            }
            if (system.advanced_options !== undefined) {
                setAdvancedOptionsState(
                    mergeAutomationSafetyIntoAdvancedOptions(
                        system.advanced_options as Record<string, unknown>
                    )
                )
            }
        }
    }, [newSystem, systemId])

    useEffect(() => {
        void getPrograms()
    }, [])

    useEffect(() => {
        if (!systemId) return
        void getCompleteSystems()
    }, [programType, devicesInSystem, systemId])

    /** Dosing UI only when a nutrient doser device is assigned to this system (not for outlets or unrelated pumps). */
    const showDosingSection = useMemo(() => {
        return devicesInSystem.some((device: { device_type?: string }) => {
            const dt = device.device_type ?? ''
            if (dt === 'Pump Device 4 Mixer') return true
            return dt.toLowerCase().includes('doser')
        })
    }, [devicesInSystem])

    /** Toggle safety only for known outlet `device_type` strings (same family DeviceController toggles). */
    const showToggleSafetySection = useMemo(() => {
        return devicesInSystem.some((device: { device_type?: string }) =>
            isOutletDeviceType(device.device_type)
        )
    }, [devicesInSystem])

    /** Hysteresis safety is primarily for temperature/humidity loops. */
    const showHysteresisSafetySection = useMemo(() => {
        return completedSystems.some((group: { variable?: string }) => {
            const variable = (group.variable ?? '').toLowerCase()
            return variable === 'temperature' || variable === 'humidity'
        })
    }, [completedSystems])

    const showAnyAutomationSafetySection =
        showToggleSafetySection || showDosingSection || showHysteresisSafetySection

    const getPrograms = async () => {
        const response = await fetch(`${getBackendUrl()}/api/programs/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        const json = await response.json()
        if (response.ok) {
            setProgramList(Array.isArray(json) ? json : [])
        }
    }

    const getCompleteSystems = async () => {
        if (user !== null && system && typeof system === 'object' && '_id' in system) {
            const systems = await checkForCompleteSystems(system, user)
            setCompletedSystems(Array.isArray(systems) ? systems : [])
        }
    }

    const handleSave = async ()  => {
        if(user){
            //We have two scenarios, creating a new system, or updating one
            if(newSystem){
                //We need to create a new system and go and update the system_id for any devices that have been assigned to this system
                let systemToCreate = {
                    user_id: user._id,
                    name: system.name,
                    description: system.description,
                    reservoirSize: system.reservoirSize,
                    programType: programType,
                    program: program,
                    manualControls: manualControls,
                    // defaultOptions: false, //This used to be for controlling if users wanted to enable advanced options by default (but now it is always false )
                    advanced_options: advancedOptionsState,
                    sensor_groups: sensorGroups,
                }
                //First we need to create the system
                const response = await fetch(`${getBackendUrl()}/api/systems/`, {
                    method: 'POST',
                    body: JSON.stringify(systemToCreate),
                    headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                    }
                })
                if(response.ok){
                    const created = await response.json()
                    const syncOk = await syncDevicesWithSystem(user, created._id, devicesInSystem)
                    if (syncOk) {
                        await fetchDevices(user, deviceDispatch)
                        await getData()
                        setSnackbarProperties({ open: true, success: true, message: 'System created successfully!' });
                    } else {
                        setSnackbarProperties({
                            open: true,
                            success: false,
                            message: 'System created but some device assignments failed. Check devices and save again.',
                        })
                    }
                }
                else{
                    setSnackbarProperties({ open: true, success: false, message: 'Failed to create system.' });
                }
            }
            else{
                //We need to update the existing system and any devices that have been assigned to it
                let systemToUpdate = {
                    name: system.name,
                    description: system.description,
                    reservoirSize: system.reservoirSize,
                    programType: programType,
                    program: program,
                    manualControls: manualControls,
                    advanced_options: advancedOptionsState,
                    sensor_groups: sensorGroups,
                }
                const response = await fetch(`${getBackendUrl()}/api/systems/`+system._id, {
                    method: 'PATCH',
                    body: JSON.stringify(systemToUpdate),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    }
                })
                if(response.ok){
                    const updatedSystem = await response.json()
                    const syncOk = await syncDevicesWithSystem(user, updatedSystem._id, devicesInSystem)
                    if (syncOk) {
                        await fetchDevices(user, deviceDispatch)
                        await getData()
                        setSystem(updatedSystem)
                        systemsDispatch({ type: 'UPDATE_SYSTEM', payload: updatedSystem })
                        setSnackbarProperties({ open: true, success: true, message: 'System updated successfully!' });
                    } else {
                        setSnackbarProperties({
                            open: true,
                            success: false,
                            message: 'System saved but device assignments failed. Try saving again.',
                        })
                    }
                }
                else{
                    setSnackbarProperties({ open: true, success: false, message: 'Failed to update system.' });
                }
            }
        }
    }

    const programOptions = useMemo<SelectOption[]>(
        () =>
            programList
                .map((p: Program & { id?: string }) => ({
                    value: String(p._id ?? p.id ?? ''),
                    label: p.name ?? 'Program',
                }))
                .filter((o) => o.value !== ''),
        [programList]
    )

    const selectedProgramOption = useMemo(() => {
        const id = String(program._id ?? (program as { id?: string }).id ?? '')
        if (!id) return null
        return programOptions.find((o) => o.value === id) ?? null
    }, [program, programOptions])

    const handleProgramChange = (opt: SingleValue<SelectOption>) => {
        if (!opt) {
            setProgram({
                _id: '',
                name: '',
                temperature: 0,
                humidity: 0,
                ec: 0,
                ph: 0,
                lights_on: 0,
            })
            return
        }
        const found = programList.find((p: Program & { id?: string }) => String(p._id ?? p.id) === opt.value)
        if (found) setProgram(found)
    }

    const deviceOptions = useMemo<SelectOption[]>(
        () =>
            devicesNotInASystem.map((device: { _id: string; name: string }) => ({
                value: device._id,
                label: device.name,
            })),
        [devicesNotInASystem]
    )

    const renderSensorGroups = () =>
        completedSystems.map((completeSystem: (typeof completedSystems)[number], index: number) => (
            <Card
                key={completeSystem.variable ?? String(index)}
                className="overflow-visible border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60"
            >
                <CardHeader className="space-y-1 border-b border-zinc-800/80 pb-3 pt-4">
                    <CardTitle className="text-lg font-semibold tracking-tight text-zinc-50">
                        {completeSystem.variable ?? 'Control'} loop
                    </CardTitle>
                    <CardDescription className="text-sm text-zinc-400">
                        Assign sensors and controllers; tolerances apply in automatic mode.
                    </CardDescription>
                </CardHeader>
                <CardContent className="overflow-visible pt-4">
                    <SensorGroup
                        system={system}
                        completedSystems={completeSystem}
                        program={program}
                        advancedOptions={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                        customStyles={settingsSelectMultiStyles}
                        setSensorGroups={setSensorGroups}
                        sensorGroups={sensorGroups}
                    />
                </CardContent>
            </Card>
        ))

    if (!settingsPortalEl) {
        return null
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
            style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                minHeight: '100svh',
            }}
            role="presentation"
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                aria-label="Close settings"
                onClick={() => setSystem(null)}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="system-settings-title"
                className={cn(
                    'relative z-10 flex max-h-[min(92dvh,880px)] w-full flex-col overflow-hidden rounded-t-2xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl sm:max-w-6xl sm:rounded-2xl'
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                        <div className="w-full min-w-0 text-zinc-100">
                            <Snackbar properties={snackbarProperties} />
                            <div className="mx-auto flex max-w-6xl flex-col gap-6 overflow-visible px-4 pb-10 pt-4">
                                <h2 id="system-settings-title" className="sr-only">
                                    System settings — {system.name}
                                </h2>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800 sm:w-auto"
                        onClick={() => setSystem(null)}
                    >
                        ← Back
                    </Button>
                    <Button
                        type="button"
                        className="w-full bg-emerald-600 font-semibold text-white hover:bg-emerald-500 sm:w-auto"
                        onClick={() => void handleSave()}
                    >
                        Save changes
                    </Button>
                </div>

                <div className="grid auto-rows-auto items-start gap-6 overflow-visible lg:grid-cols-12">
                    <Card className="border-zinc-700 bg-zinc-900/90 shadow-lg lg:col-span-5">
                        <CardHeader>
                            <CardTitle className="text-lg text-zinc-50">Basics</CardTitle>
                            <CardDescription className="text-zinc-400">
                                Name, description, and reservoir for this environment.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <label className="system-setting-basic block">
                                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Environment name
                                </span>
                                <input
                                    type="text"
                                    placeholder="Environment name"
                                    value={system.name}
                                    onChange={(e) => setSystem({ ...system, name: e.target.value })}
                                    className="w-full rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-zinc-50"
                                />
                            </label>
                            <label className="system-setting-basic block">
                                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Description
                                </span>
                                <input
                                    type="text"
                                    placeholder="Description"
                                    value={system.description ?? ''}
                                    onChange={(e) => setSystem({ ...system, description: e.target.value })}
                                    className="w-full rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-zinc-50"
                                />
                            </label>
                            <label className="system-setting-basic block">
                                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Reservoir size
                                </span>
                                <input
                                    type="number"
                                    placeholder="Reservoir size"
                                    value={system.reservoirSize ?? ''}
                                    onChange={(e) => setSystem({ ...system, reservoirSize: e.target.value })}
                                    className="w-full rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-zinc-50"
                                />
                            </label>
                        </CardContent>
                    </Card>

                    <Card className="overflow-visible border-zinc-700 bg-zinc-900/90 shadow-lg lg:col-span-7">
                        <CardHeader>
                            <CardTitle className="text-lg text-zinc-50">Devices</CardTitle>
                            <CardDescription className="text-zinc-400">
                                Add unassigned hardware or remove devices from this system.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 overflow-visible">
                            <div className="relative z-10 min-w-0 space-y-2 overflow-visible">
                                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Add devices
                                </span>
                                <Select<SelectOption, true>
                                    instanceId="system-settings-add-devices"
                                    inputId="system-settings-add-devices-input"
                                    classNamePrefix="hydro-sys-devices"
                                    options={deviceOptions}
                                    isMulti
                                    isSearchable
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    blurInputOnSelect={false}
                                    captureMenuScroll={false}
                                    menuShouldScrollIntoView
                                    placeholder="Search devices to add…"
                                    noOptionsMessage={() => 'No unassigned devices'}
                                    value={selectedDevices}
                                    styles={settingsSelectMultiStyles}
                                    menuPlacement="auto"
                                    onChange={(selected: MultiValue<SelectOption>) =>
                                        setSelectedDevices(selected ? [...selected] : [])
                                    }
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="bg-zinc-800 text-zinc-50 hover:bg-zinc-700"
                                    onClick={() => {
                                        const newDevices = devicesNotInASystem.filter((device) =>
                                            selectedDevices.some(
                                                (selected: { value: string }) => selected.value === device._id
                                            )
                                        )
                                        const updatedDevicesNotInSystem = devicesNotInASystem.filter(
                                            (device) =>
                                                !newDevices.some((newDevice) => newDevice._id === device._id)
                                        )
                                        setDevicesInSystem([...devicesInSystem, ...newDevices])
                                        setDevicesNotInASystem(updatedDevicesNotInSystem)
                                        setSelectedDevices([])
                                    }}
                                >
                                    Add selected
                                </Button>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-zinc-700">
                                <table className="device-table min-w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devicesInSystem.map((device, index) => (
                                            <tr key={device._id ?? index}>
                                                <td>{device.name}</td>
                                                <td>{device.device_type}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="remove-device-button"
                                                        onClick={() => {
                                                            const updatedDevices = devicesInSystem.filter(
                                                                (_, i) => i !== index
                                                            )
                                                            setDevicesInSystem(updatedDevices)
                                                            setDevicesNotInASystem([
                                                                ...devicesNotInASystem,
                                                                devicesInSystem[index],
                                                            ])
                                                        }}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="overflow-visible border-zinc-700 bg-zinc-900/90 shadow-lg lg:col-span-12">
                        <CardHeader>
                            <CardTitle className="text-lg text-zinc-50">Programming</CardTitle>
                            <CardDescription className="text-zinc-400">
                                Manual: direct device control. Automatic: recipe targets and control loops when
                                hardware is complete.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6 overflow-visible">
                            <div
                                className={cn(
                                    'inline-flex w-full max-w-md rounded-lg border border-zinc-600 bg-zinc-950 p-1 sm:w-auto'
                                )}
                                role="group"
                                aria-label="Program type"
                            >
                                <Button
                                    type="button"
                                    variant={programType === 'Manual' ? 'default' : 'ghost'}
                                    className={cn(
                                        'flex-1 rounded-md sm:flex-none',
                                        programType === 'Manual' &&
                                            'bg-emerald-600 text-white hover:bg-emerald-500'
                                    )}
                                    onClick={() => setProgramType('Manual')}
                                >
                                    Manual
                                </Button>
                                <Button
                                    type="button"
                                    variant={programType === 'Automatic' ? 'default' : 'ghost'}
                                    className={cn(
                                        'flex-1 rounded-md sm:flex-none',
                                        programType === 'Automatic' &&
                                            'bg-emerald-600 text-white hover:bg-emerald-500'
                                    )}
                                    onClick={() => setProgramType('Automatic')}
                                >
                                    Automatic
                                </Button>
                            </div>

                            {programType === 'Automatic' ? (
                                <div className="space-y-4">
                                    <div className="relative z-10 max-w-xl min-w-0 space-y-2 overflow-visible">
                                        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                                            Grow program
                                        </span>
                                        <Select<SelectOption, false>
                                            instanceId="system-settings-program"
                                            inputId="system-settings-program-input"
                                            classNamePrefix="hydro-sys-program"
                                            options={programOptions}
                                            placeholder="Select a program…"
                                            isSearchable
                                            styles={settingsSelectSingleStyles}
                                            value={selectedProgramOption}
                                            onChange={handleProgramChange}
                                            menuPlacement="auto"
                                            captureMenuScroll={false}
                                            menuShouldScrollIntoView
                                            isClearable
                                            blurInputOnSelect
                                        />
                                    </div>
                                    {program._id || (program as { id?: string }).id ? (
                                        <div className="overflow-x-auto rounded-lg border border-zinc-700">
                                            <table className="device-table program-table min-w-full text-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Temp (°F)</th>
                                                        <th>Humidity (%)</th>
                                                        <th>EC (mS)</th>
                                                        <th>pH</th>
                                                        <th>Lights on (hrs)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td data-label="Name">{program.name}</td>
                                                        <td data-label="Temp (°F)">{program.temperature}</td>
                                                        <td data-label="Humidity (%)">{program.humidity}</td>
                                                        <td data-label="EC (mS)">{program.ec}</td>
                                                        <td data-label="pH">{program.ph}</td>
                                                        <td data-label="Lights On (hrs)">{program.lights_on}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-zinc-500">Choose a program to view targets.</p>
                                    )}
                                </div>
                            ) : (
                                <ManualProgram
                                    environment={system}
                                    programType={programType}
                                    manualControls={manualControls}
                                    setManualControls={setManualControls}
                                />
                            )}
                        </CardContent>
                    </Card>

                    {programType === 'Automatic' && completedSystems.length > 0 ? (
                        <div className="space-y-3 lg:col-span-12">
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">
                                Control loops
                            </h3>
                            <div className="grid auto-rows-auto gap-4 md:grid-cols-2 xl:grid-cols-2">
                                {renderSensorGroups()}
                            </div>
                        </div>
                    ) : null}

                    {programType === 'Automatic' && completedSystems.length > 0 && showAnyAutomationSafetySection ? (
                        <Card className="border-zinc-700 bg-zinc-900/90 shadow-lg lg:col-span-12">
                            <CardHeader>
                                <CardTitle className="text-lg text-zinc-50">
                                    Local automation limits (DeviceController)
                                </CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Safety caps for the Python controller on your network: separate limits for
                                    smart plugs vs doser/mixer runs, plus climate deadband. Saved with this
                                    system in the database.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <AutomationSafetySettings
                                    advancedOptionsState={advancedOptionsState}
                                    setAdvancedOptionsState={setAdvancedOptionsState}
                                    showToggleSection={showToggleSafetySection}
                                    showDoseSection={showDosingSection}
                                    showHysteresisSection={showHysteresisSafetySection}
                                />
                            </CardContent>
                        </Card>
                    ) : null}

                    {showDosingSection ? (
                        <Card className="border-zinc-700 bg-zinc-900/90 shadow-lg lg:col-span-12">
                            <CardHeader>
                                <CardTitle className="text-lg text-zinc-50">Dosing</CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Nutrient timing and mixing (defaults can be tuned per grow).
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <DoserSettings
                                    advancedOptionsState={advancedOptionsState}
                                    setAdvancedOptionsState={setAdvancedOptionsState}
                                />
                            </CardContent>
                        </Card>
                    ) : null}
                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        settingsPortalEl
    )
}
 
export default SystemSettings;